import type {
  BulkFieldMode,
  BulkSectorSpecialtyRequest
} from '@/Network/Types/Requests/StationRequests'
import type {
  BulkSectorSpecialtyResponse,
  StationSectorRef,
  StationSpecialtyRef
} from '@/Network/Types/Responses/StationResponses'
import {
  specialtySelectionKey,
  toSpecialtyInput,
  type SpecialtySelection
} from '@/utils/sectorSpecialty'
import { catalogDisplayName } from '@/utils/stationDisplay'
import type { GridCardRef } from './gridModel'

/** Plantão dentro de um lote (grade H7 ou Consulta H8-b). */
export type BulkItem = {
  stationId: number
  healthUnitId: number | null
  companyId: number | null
  sector: StationSectorRef | null
  specialty: StationSpecialtyRef | null
  /** Escala de ORIGEM: o "Mover" checa `station_editing` nela e pula quem já está no destino. */
  scheduleId?: number | null
}

export type BulkFieldState<T> = { mode: BulkFieldMode; value: T | null }

export type FieldPreview = {
  overwritten: number
  filled: number
  unchanged: number
  cleared: number
  alreadyEmpty: number
}

/** Card da grade → item do lote. Sem valor no card = `null` (nunca inferido). */
export const toBulkItem = (ref: GridCardRef): BulkItem => ({
  stationId: ref.card.stationId,
  healthUnitId: ref.unit.healthUnitId,
  companyId: ref.companyId,
  sector: ref.card.sector ?? null,
  specialty: ref.card.specialty ?? null,
  scheduleId: ref.scheduleId
})

const specialtyRefKey = (ref: StationSpecialtyRef | null): string | null =>
  ref ? `${ref.source === 'official' ? 'o' : 'c'}:${ref.id}` : null

function preview(
  items: BulkItem[],
  currentKey: (item: BulkItem) => string | null,
  mode: BulkFieldMode,
  valueKey: string | null
): FieldPreview {
  const out: FieldPreview = { overwritten: 0, filled: 0, unchanged: 0, cleared: 0, alreadyEmpty: 0 }
  items.forEach((item) => {
    const key = currentKey(item)
    if (mode === 'keep' || (mode === 'set' && !valueKey)) out.unchanged += 1
    else if (mode === 'clear') {
      if (key) out.cleared += 1
      else out.alreadyEmpty += 1
    } else if (key === valueKey) out.unchanged += 1
    else if (key) out.overwritten += 1
    else out.filled += 1
  })
  return out
}

export const previewSector = (
  items: BulkItem[],
  state: BulkFieldState<StationSectorRef>
): FieldPreview =>
  preview(
    items,
    (item) => (item.sector ? `s:${item.sector.id}` : null),
    state.mode,
    state.value ? `s:${state.value.id}` : null
  )

export const previewSpecialty = (
  items: BulkItem[],
  state: BulkFieldState<SpecialtySelection>
): FieldPreview =>
  preview(
    items,
    (item) => specialtyRefKey(item.specialty),
    state.mode,
    specialtySelectionKey(state.value)
  )

/** "1 plantão" / "2 plantões": helper único de singular/plural do lote e do mover. */
export const pluralize = (n: number, one: string, many: string): string =>
  `${n} ${n === 1 ? one : many}`

export function describeFieldPreview(
  p: FieldPreview,
  mode: BulkFieldMode,
  hasValue: boolean,
  label: 'setor' | 'especialidade'
): string {
  const article = label === 'setor' ? 'o setor' : 'a especialidade'
  if (mode === 'keep') return `Nenhum plantão muda de ${label}.`
  if (mode === 'clear') {
    const rest = p.alreadyEmpty
      ? ` · ${p.alreadyEmpty} já ${p.alreadyEmpty === 1 ? 'está' : 'estão'} sem ${label}`
      : ''
    return `${pluralize(p.cleared, 'plantão perderá', 'plantões perderão')} ${article}${rest}.`
  }
  if (!hasValue)
    return `Escolha ${label === 'setor' ? 'um setor' : 'uma especialidade'} para ver a prévia.`
  return `${pluralize(p.overwritten, 'será sobrescrito', 'serão sobrescritos')} · ${pluralize(
    p.filled,
    'vai receber',
    'vão receber'
  )} o valor · ${p.unchanged} já ${p.unchanged === 1 ? 'está igual' : 'estão iguais'}.`
}

/** Contadores do BE (prévia `dryRun` ou confirmação), no singular/plural certo. */
export function describeBulkCounts(result: BulkSectorSpecialtyResponse): string {
  return `${pluralize(result.overwritten, 'sobrescrito', 'sobrescritos')}, ${pluralize(
    result.filled,
    'preenchido',
    'preenchidos'
  )}`
}

/** Prévia autoritativa do BE (`dryRun: true`, DECISIONS A8). */
export function describeServerPreview(result: BulkSectorSpecialtyResponse): string {
  if (!result.updated) return 'Nenhum plantão muda.'
  return `${pluralize(result.updated, 'plantão será atualizado', 'plantões serão atualizados')} (${describeBulkCounts(result)}).`
}

/** Toast da confirmação, com os números devolvidos pelo BE. */
export const describeBulkApplied = (result: BulkSectorSpecialtyResponse): string =>
  `${pluralize(result.updated, 'plantão atualizado', 'plantões atualizados')} (${describeBulkCounts(result)}).`

type SectorPart = BulkSectorSpecialtyRequest['sector']
type SpecialtyPart = BulkSectorSpecialtyRequest['specialty']

/** `undefined` = "Definir" sem valor (não dá para enviar). */
function sectorPart(state: BulkFieldState<StationSectorRef>): SectorPart | undefined {
  if (state.mode !== 'set') return { mode: state.mode }
  return state.value ? { mode: 'set', id: state.value.id } : undefined
}

function specialtyPart(state: BulkFieldState<SpecialtySelection>): SpecialtyPart | undefined {
  if (state.mode !== 'set') return { mode: state.mode }
  const value = toSpecialtyInput(state.value)
  return value ? { mode: 'set', value } : undefined
}

/**
 * Corpo do `PATCH /stations/bulk/sector-specialty`. `null` = nada a enviar: os dois campos
 * em Manter, ou "Definir" sem valor escolhido (o lote nunca sugere valores, P2).
 */
export function buildBulkRequest(
  ids: number[],
  sector: BulkFieldState<StationSectorRef>,
  specialty: BulkFieldState<SpecialtySelection>
): BulkSectorSpecialtyRequest | null {
  if (!ids.length || (sector.mode === 'keep' && specialty.mode === 'keep')) return null
  const sectorBody = sectorPart(sector)
  const specialtyBody = specialtyPart(specialty)
  if (!sectorBody || !specialtyBody) return null
  return { ids, sector: sectorBody, specialty: specialtyBody }
}

/** Q30: ao mudar de unidade o BE limpa o setor; só conta quem tinha setor. */
export const countSectorClearedOnMove = (items: BulkItem[], destinationUnitId: number): number =>
  items.filter((item) => !!item.sector && item.healthUnitId !== destinationUnitId).length

/**
 * A9: ao mudar de empresa o BE limpa a especialidade DA EMPRESA (a oficial é global e fica).
 * Destino sem empresa conhecida → 0 (o toast usa `specialtyCleared` da resposta).
 */
export const countSpecialtyClearedOnMove = (
  items: BulkItem[],
  destinationCompanyId: number | null
): number =>
  destinationCompanyId === null
    ? 0
    : items.filter(
        (item) =>
          item.specialty?.source === 'company' &&
          item.companyId !== null &&
          item.companyId !== destinationCompanyId
      ).length

export function currentValueSummary(items: BulkItem[], field: 'setor' | 'especialidade'): string[] {
  const counts = new Map<string, number>()
  items.forEach((item) => {
    const ref = field === 'setor' ? item.sector : item.specialty
    const label = catalogDisplayName(ref, field === 'setor' ? 'Sem setor' : 'Sem especialidade')
    counts.set(label, (counts.get(label) ?? 0) + 1)
  })
  return [...counts.entries()].map(([label, n]) => `${label} · ${n}`)
}
